import { cx } from 'cva'
import Link from 'next/link'
import type { ComponentProps, FC, MouseEvent } from 'react'

export type LinkTabType = {
	href: string
	label: string
}

export type LinkTabProps = LinkTabType &
	ComponentProps<typeof Link> & {
		scrollOffset?: number
		isActive?: boolean
		slideTo?: () => void
		scrollFn?: (y: number) => void
	}

const defaultScrollFn = (y: number) => {
	window.scrollTo({
		top: y,
		behavior: 'smooth',
	})
}

export const LinkTab: FC<LinkTabProps> = ({
	href,
	label,
	scrollOffset = 0,
	isActive = false,
	slideTo,
	scrollFn = defaultScrollFn,
	...props
}) => {
	const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
		// Only handle anchor links
		if (href.startsWith('#')) {
			e.preventDefault()

			const target = document.getElementById(href.slice(1))
			if (target) {
				const elementPosition = target.getBoundingClientRect().top
				const offsetPosition = elementPosition + window.scrollY - scrollOffset
				scrollFn(offsetPosition)
			}
			slideTo?.()
		}
	}

	return (
		<Link
			href={href}
			prefetch={!href.startsWith('#')}
			onClick={handleClick}
			className={cx(
				'relative block cursor-pointer pb-xl text-button text-blue transition-colors duration-200',
				'group hover:text-blue-shade-70 focus:text-blue-shade-70',
				isActive && 'text-blue-shade-70',
			)}
			{...props}
		>
			{label}
			<span
				className={cx(
					'absolute bottom-0 left-0 block w-full bg-blue transition-all duration-200',
					isActive
						? 'h-(length:--stroke-lg)'
						: 'h-0 group-hover:h-(length:--stroke-lg) group-active:h-(length:--stroke-lg)',
				)}
			/>
		</Link>
	)
}

export default LinkTab
